// Copyright (C) 2021 Bystronic Laser AG

import moment from 'moment-timezone';
import { FilterTimeAxisSpanEnum } from '@/models/enums/FilterTimeAxisSpanEnum';
import i18n from '@/i18n';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { Logger } from '@/utils/logger';
import { currentCustomer } from '@/utils/permissionUtils';
import { isNil } from './misc';

/** Returns if value is a Date instance with a valid value. */
export function isDate(value: any): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

export function endOfYear(): Date {
  return moment().endOf('year').toDate();
}

export function yearToDate(): [Date, Date] {
  return [moment().startOf('year').toDate(), new Date()];
}

export function today(): Date {
  return moment()
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .toDate();
}

// HACK: moment.tz.guess caches the values across unit tests. This allows it to
// be disabled when running unit tests.
let shouldMomentIgnoreCachedTimeZoneGuess = false;

// Set up calls to moment.tz.guess to ignore cached values.
export function ignoreTimeZoneCachedGuess() {
  shouldMomentIgnoreCachedTimeZoneGuess = true;
}

/** Returns the user's time zone according to the browser as a IANA string. */
export function userTimeZone(): string {
  return moment.tz.guess(shouldMomentIgnoreCachedTimeZoneGuess);
}

export function timeSpanToStartOfUnitOfTime(
  timeSpan: FilterTimeSpanEnum,
): moment.unitOfTime.StartOf {
  const unitOfTime = timeSpanToUnitOfTime(timeSpan);

  // We want weeks to start always on Mondays (ISO, $god bless you!),
  // independently of the user locale, to match the week interval from
  // Timescale (PostgreSQL).
  return unitOfTime === 'week' ? 'isoWeek' : unitOfTime;
}

export function timeSpanToUnitOfTime(
  timeSpan: FilterTimeSpanEnum,
): moment.unitOfTime.DurationConstructor {
  switch (timeSpan) {
    case FilterTimeSpanEnum.Day:
      return 'day';
    case FilterTimeSpanEnum.Week:
      return 'week';
    case FilterTimeSpanEnum.Month:
      return 'month';
    case FilterTimeSpanEnum.Year:
      return 'year';
    case FilterTimeSpanEnum.None:
      Logger.error('Time span should never be "None"');
      throw new Error('Time span should never be "None"');
  }
}

export function dateRangeToDateStrings(
  dateRange: [Date, Date],
): [string, string] {
  const transform = (date: Date) =>
    moment(date)
      .subtract(date.getTimezoneOffset(), 'minutes')
      .format('YYYY-MM-DD');
  return [transform(dateRange[0]), transform(dateRange[1])];
}

/** Converts a Date into a ISO string with just the date part (YYYY-MM-DD). */
export function dateToShortISOString(date: Date): string {
  return moment(date).format('YYYY-MM-DD');
}

/** Converts a Date into a string with date and time. */
export function formatDateWithTime(date: Date): string {
  return moment(date).format('YYYY-MM-DD, HH:mm');
}

/** Formats a Date into a string with date and time for the customer's time zone. */
export function formatDateWithTimeOnCustomerTimeZone(date: Date): string {
  return moment(date)
    .tz(currentCustomer()!.timeZone)
    .format('YYYY-MM-DD, HH:mm');
}

export function groupByDate(
  data: any[],
  dates: string[],
  groupingFunction: (groupObject: any, currentDataItem: any) => any,
  dateField = 'date',
) {
  return dates.map((date) => {
    const groupValues = data
      .filter((dataItem) => dataItem[dateField] === date)
      .reduce(groupingFunction, {});

    return {
      date,
      ...groupValues,
    };
  });
}

export function translateDateGrouping(
  dateGrouping: FilterTimeAxisSpanEnum,
): string {
  const dateGroupingKey = FilterTimeAxisSpanEnum[dateGrouping];
  return i18n.t(`timespans.${dateGroupingKey.toLowerCase()}`).toString();
}

export function lastDayOfNextMonth(date: Date): Date {
  date = moment(date).add(1, 'month').endOf('month').toDate();
  // Directly returning the date from m.toDate() would somehow make the
  // datepicker show the next day as the minimum day
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatDuration(durationSeconds: number | null): string {
  if (isNil(durationSeconds)) {
    return '-';
  }

  const duration = moment.duration(durationSeconds, 'seconds');
  if (duration.months() > 0) {
    return convertSecondsToMonthsDaysHours(duration);
  } else if (duration.days() > 0) {
    return convertSecondsToDaysHoursMinutes(duration);
  } else if (duration.hours() > 0) {
    return moment.unix(durationSeconds).utcOffset(0).format('H[h] m[m]');
  } else if (duration.minutes() > 0) {
    return moment.unix(durationSeconds).format('m[m] s[s]');
  } else {
    return moment.unix(durationSeconds).format('s[s]');
  }
}

export function convertSecondsToDaysHoursMinutes(
  duration: moment.Duration,
): string {
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();

  return `${days}d ${hours}h ${minutes}m`;
}

function convertSecondsToMonthsDaysHours(duration: moment.Duration): string {
  return `${duration.months()}M ${duration.days()}d ${duration.hours()}h`;
}

export function getMaxSecondsByTimeFilter(
  timeFilter: FilterTimeSpanEnum,
): number {
  const SECONDS_IN_A_DAY = 24 * 60 * 60;
  switch (timeFilter) {
    case FilterTimeSpanEnum.Day:
      return SECONDS_IN_A_DAY;
    case FilterTimeSpanEnum.Week:
      return SECONDS_IN_A_DAY * 7;
    case FilterTimeSpanEnum.Month:
      return moment().diff(moment().subtract(1, 'month'), 'seconds');
    case FilterTimeSpanEnum.Year:
      return moment().diff(moment().subtract(1, 'year'), 'seconds');
    default:
      return 0;
  }
}

export function formatTimeAgo(date: Date, now: Date = new Date()): string {
  const thisMoment = moment(now);
  if (thisMoment.diff(date, 'hours') > 0) {
    return i18n.tc('time_msg.hours', moment().diff(date, 'hours')).toString();
  } else if (thisMoment.diff(date, 'minutes') > 0) {
    return i18n
      .tc('time_msg.minutes', moment().diff(date, 'minutes'))
      .toString();
  } else {
    return i18n
      .tc('time_msg.seconds', moment().diff(date, 'seconds'))
      .toString();
  }
}

export function formatDateForDateGrouping(
  date: string | number | Date | moment.Moment,
  dateGrouping: FilterTimeAxisSpanEnum,
): string {
  const m = moment(date);
  switch (dateGrouping) {
    case FilterTimeAxisSpanEnum.Day:
      return m.format('YYYY-MM-DD');
    case FilterTimeAxisSpanEnum.Week:
      return m.format('YYYY-[W]ww');
    case FilterTimeAxisSpanEnum.Month:
      return m.format('YYYY-MM');
    case FilterTimeAxisSpanEnum.Quarter:
      return m.format('YYYY-[Q]Q');
    case FilterTimeAxisSpanEnum.Year:
      return m.format('YYYY');
    default:
      return m.format('YYYY-MM-DD, HH:mm');
  }
}
